/**
 * MealDistributionCard.jsx
 * Admin-Komponente für Standard-Mahlzeiten-Verteilung
 * - Standard-Prozentsätze pro Mahlzeit festlegen
 * - Gilt für alle Kunden ohne individuelle Einstellung
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { 
  Utensils, Coffee, Sun, Moon, Cookie, 
  RefreshCw, Save, AlertTriangle, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MEAL_CONFIG = [
  { key: 'breakfast', label: 'Frühstück', icon: Coffee, color: 'bg-amber-500', lightColor: 'bg-amber-100' },
  { key: 'lunch', label: 'Mittagessen', icon: Sun, color: 'bg-orange-500', lightColor: 'bg-orange-100' },
  { key: 'dinner', label: 'Abendessen', icon: Moon, color: 'bg-indigo-500', lightColor: 'bg-indigo-100' },
  { key: 'snacks', label: 'Snacks', icon: Cookie, color: 'bg-green-500', lightColor: 'bg-green-100' }
];

const MealDistributionCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [distribution, setDistribution] = useState({
    breakfast: 30,
    lunch: 35,
    dinner: 25,
    snacks: 10
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/meal-distribution`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDistribution({
        breakfast: response.data.breakfast || 30,
        lunch: response.data.lunch || 35,
        dinner: response.data.dinner || 25,
        snacks: response.data.snacks || 10
      });
    } catch (error) {
      console.error('Error loading meal distribution:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/meal-distribution`,
        distribution,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mahlzeiten-Verteilung gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    setDistribution({ ...distribution, [key]: numValue });
  };

  const total = distribution.breakfast + distribution.lunch + distribution.dinner + distribution.snacks;
  const isOverLimit = total > 100;
  const isUnderLimit = total < 100;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <Utensils className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <CardTitle>Mahlzeiten-Verteilung</CardTitle>
            <CardDescription>
              Standard-Verteilung der Tageskalorien auf Mahlzeiten
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Visual Distribution Bar */}
        <div className="space-y-2">
          <div className="flex h-8 rounded-lg overflow-hidden">
            {MEAL_CONFIG.map((meal) => (
              <div
                key={meal.key}
                className={`${meal.color} flex items-center justify-center text-white text-xs font-medium transition-all`}
                style={{ width: `${(distribution[meal.key] / Math.max(total, 100)) * 100}%` }}
              >
                {distribution[meal.key] > 10 && `${distribution[meal.key]}%`}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Meal Inputs */}
        <div className="grid grid-cols-2 gap-4">
          {MEAL_CONFIG.map((meal) => {
            const Icon = meal.icon;
            return (
              <div key={meal.key} className={`p-4 rounded-lg ${meal.lightColor} border`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" />
                  <Label className="font-medium">{meal.label}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={distribution[meal.key]}
                    onChange={(e) => handleChange(meal.key, e.target.value)}
                    className="bg-white"
                  />
                  <span className="text-sm font-medium">%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total Display */}
        <div className={`p-4 rounded-lg border ${
          isOverLimit ? 'bg-yellow-50 border-yellow-300' : 
          isUnderLimit ? 'bg-blue-50 border-blue-300' : 
          'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOverLimit ? (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              <span className="font-medium">Gesamt</span>
            </div>
            <span className={`text-2xl font-bold ${
              isOverLimit ? 'text-yellow-700' : 
              isUnderLimit ? 'text-blue-700' : 
              'text-green-700'
            }`}>
              {total}%
            </span>
          </div>
          {isOverLimit && (
            <p className="text-sm text-yellow-600 mt-2">
              Die Summe übersteigt 100%. Kunden werden als "zu viel gegessen" angezeigt, wenn sie alle Mahlzeiten erfüllen.
            </p>
          )}
          {isUnderLimit && (
            <p className="text-sm text-blue-600 mt-2">
              Die Summe ist unter 100%. Die restlichen {100 - total}% bleiben als Reserve.
            </p>
          )}
        </div>

        {/* Example Calculation */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2 font-medium">Beispiel bei 2000 kcal/Tag:</p>
          <div className="grid grid-cols-4 gap-2 text-center">
            {MEAL_CONFIG.map((meal) => (
              <div key={meal.key}>
                <p className="text-lg font-bold text-gray-900">
                  {Math.round(2000 * distribution[meal.key] / 100)}
                </p>
                <p className="text-xs text-gray-500">{meal.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MealDistributionCard;
